import { FC, useMemo } from 'react';
import styles from './SamplePlanTable.module.scss';
import { RiSquareFill } from 'react-icons/ri';
import { GroupTarget } from '@/model/SamplePlan.model';
import {
  SampleDefinitionRowData,
  GroupMap,
  UnassignedGroup,
  optionSet,
  TableOptions,
  useSamplePlanTableContext,
  useOpenSection,
} from '@/components/Samples/SamplePlanTable/SamplePlanTableProvider';
import { _isNil, _notNil } from '@/littledash';
import { ChevronCell } from './ChevronCell';
import { DetailCell } from './DetailCell';
import { MetadataCell } from './MetadataCell';
import { CalculateTimepointCell } from './CalculateTimepointCell';
import { AnimalRows } from './AnimalRows';

const groupApiIdComparator =
  (group_map: GroupMap) =>
  (left: GroupTarget['api_id'] | 'unassigned', right: GroupTarget['api_id'] | 'unassigned'): number => {
    if (left === 'unassigned') {
      return right === 'unassigned' ? 0 : -1;
    }
    if (right === 'unassigned') {
      return 1;
    }
    return (group_map.get(left)?.no ?? 0) - (group_map.get(right)?.no ?? 0);
  };

const GroupRow: FC<{
  group_api_id: GroupTarget['api_id'] | UnassignedGroup;
  data: SampleDefinitionRowData;
}> = ({ group_api_id, data }) => {
  const { group_map, group_animals, sample_definition_counts, active_details, active_metadata, table_options } =
    useSamplePlanTableContext();
  const animalsOpen = useOpenSection('group', group_api_id);
  const rowData = useMemo(() => {
    const globalCounts = sample_definition_counts.get(data.api_id);
    const groupCounts = globalCounts?.[group_api_id];

    if (group_api_id === 'unassigned') {
      group_animals.get('unassigned');
      return {
        no: undefined,
        name: 'Unassigned',
        color: '#ffffff',
        planned: groupCounts?.planned ?? 0,
        registered: groupCounts?.registered ?? 0,
        capacity: globalCounts?.capacity ?? 0,
        samplesPlanned: groupCounts?.samplesPlanned ?? 0,
        samplesCollected: groupCounts?.samplesCollected ?? 0,
      };
    }
    const group = group_map.get(group_api_id);
    if (_notNil(group)) {
      return {
        no: group.no,
        name: group.name,
        color: group.color,
        planned: groupCounts?.planned ?? 0,
        registered: groupCounts?.registered ?? 0,
        capacity: globalCounts?.capacity ?? 0,
        samplesPlanned: groupCounts?.samplesPlanned ?? 0,
        samplesCollected: groupCounts?.samplesCollected ?? 0,
      };
    }
    return null;
  }, [data.api_id, group_map, group_animals, sample_definition_counts, group_api_id]);
  if (_isNil(rowData)) {
    return null;
  }

  return (
    <>
      <div key={group_api_id} className={styles.GroupRow} data-test-component="GroupRow" data-test-element="container">
        {optionSet(TableOptions.display_selection_column, table_options) ? <div></div> : null}
        <ChevronCell type="group" className="ml3 f5" id={group_api_id} />
        <div></div>
        <div className="flex flex-row items-center gap-h-1">
          {_notNil(rowData.no) && <span>{`${rowData.no} - `}</span>}
          <RiSquareFill color={rowData.color} data-test-element="color-indicator" />
          <span>{rowData.name}</span>
        </div>
        {optionSet(TableOptions.display_planned_animal_column, table_options) ? <div>{rowData.planned}</div> : null}
        {optionSet(TableOptions.display_registered_animal_column, table_options) ? (
          <div>
            {(data.target.type === 'groups' || data.target.type === 'planned_animals') && rowData.planned > 0
              ? `${rowData.registered} / ${rowData.planned}`
              : `${rowData.registered}`}
          </div>
        ) : null}
        {optionSet(TableOptions.collected_samples_count, table_options) ? (
          <>
            <div>{rowData.samplesPlanned}</div>
            <div>{rowData.samplesCollected}</div>
          </>
        ) : null}
        <div>{data.sample_type_name}</div>
        <div>{data.timepoints}</div>
        {optionSet(TableOptions.hide_calculate_collection_time_column, table_options) ? null : (
          <div>
            <CalculateTimepointCell value={data.calculate_collection_time} />
          </div>
        )}
        {Array.from(active_details).map((detail) => (
          <DetailCell key={detail} detail={data.details?.[detail]} />
        ))}
        {Array.from(active_metadata).map((api_id) => (
          <MetadataCell key={api_id} type={api_id} value={data.metadata?.[api_id]} />
        ))}
      </div>
      {animalsOpen ? <AnimalRows data={data} group_api_id={group_api_id} /> : null}
    </>
  );
};

export const GroupRows: FC<{ data: SampleDefinitionRowData }> = ({ data }) => {
  const { sample_definition_groups, group_map } = useSamplePlanTableContext();

  const groupIds = sample_definition_groups.get(data.api_id);
  if (_isNil(groupIds) || (groupIds?.size ?? 0) === 0) {
    return null;
  }
  return (
    <>
      {Array.from(groupIds)
        .sort(groupApiIdComparator(group_map))
        .map((group_api_id) => (
          <GroupRow key={group_api_id} data={data} group_api_id={group_api_id} />
        ))}
    </>
  );
};
